import { useEarlyDepositsPaged, useExpandedRows } from '@/services/early-deposit.service';
import { watchEffect } from 'vue';

/**
 * Deep-link support for the EMD table: when the page is loaded with a `?preFile=<id>` query
 * param, expand and scroll to that row once it appears in the current page, then clear the
 * param. Wires the shared `useEarlyDepositsPaged` (page data + highlight id) and
 * `useExpandedRows` (expanded state) composables. Must be called from a component's setup so
 * the effect is bound to the component lifecycle.
 */
export const usePreFileHighlight = () => {
    const { reactivePageData, highlightPreFileId } = useEarlyDepositsPaged();
    const { expandedRows } = useExpandedRows();

    if (!highlightPreFileId.value) {
        return;
    }

    // With `flush: 'post'` the first run is deferred until the table is mounted and every
    // re-run happens after the rows are in the DOM, so the target row can be scrolled to directly.
    const stopHighlightEffect = watchEffect(
        () => {
            if (!highlightPreFileId.value) {
                return;
            }

            const targetEmd = reactivePageData.emds?.find(emd => String(emd.id) === highlightPreFileId.value);
            if (!targetEmd) {
                return;
            }

            expandedRows.value = { ...expandedRows.value, [targetEmd.id]: true };

            document.querySelector(`.row-id-${targetEmd.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Clear the highlight param so it doesn't interfere with subsequent fetches
            highlightPreFileId.value = '';

            // Clean up the URL query param without page reload
            const url = new URL(window.location.href);
            url.searchParams.delete('preFile');
            window.history.replaceState({}, '', url.toString());

            stopHighlightEffect();
        },
        { flush: 'post' },
    );
};
